import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useMap } from '../../features/mapping/MapContext';
import { useApi } from '../../api/api';
import { useEditModeLookups } from '../../contexts/EditModeLookupsContext';
import Modal from '../common/Modal/Modal';
import './MoreEditOptionsModal.css';

const DEFAULT_CAMERA = {
  camera_lat: 0,
  camera_lon: 0,
  camera_height_m: 0,
  camera_heading: 0,
  camera_pitch: 0,
  camera_roll: 0,
};

const CAMERA_FIELDS = [
  { key: 'camera_lat', label: 'Camera Lat' },
  { key: 'camera_lon', label: 'Camera Lon' },
  { key: 'camera_height_m', label: 'Camera Height (m)' },
  { key: 'camera_heading', label: 'Camera Heading' },
  { key: 'camera_pitch', label: 'Camera Pitch' },
  { key: 'camera_roll', label: 'Camera Roll' },
];

const CREATE_COMPONENT_FIELDS = [
  { key: 'component_slug', label: 'Component Slug', required: true },
  { key: 'kind', label: 'Kind', required: true },
  { key: 'name', label: 'Name', required: false },
  { key: 'description', label: 'Description', required: false },
];

function getCurrentCameraFromViewer(viewer) {
  if (!viewer || !window.Cesium) return null;
  const Cesium = window.Cesium;
  const cam = viewer.camera;
  const cartographic = Cesium.Cartographic.fromCartesian(cam.position);
  return {
    camera_lat: Cesium.Math.toDegrees(cartographic.latitude),
    camera_lon: Cesium.Math.toDegrees(cartographic.longitude),
    camera_height_m: cartographic.height,
    camera_heading: cam.heading,
    camera_pitch: cam.pitch,
    camera_roll: cam.roll,
  };
}

const DEFAULT_CREATE_COMPONENT = {
  component_slug: '',
  kind: '',
  name: '',
  description: '',
};

const DEFAULT_CREATE_UI_SECTION = {
  slug: '',
  title: '',
};

/** Extract a displayable string from API error (handles FastAPI validation detail array). */
function getErrorMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    const msg = first?.msg ?? first?.message;
    const loc = first?.loc?.filter(Boolean)?.join('.');
    return typeof msg === 'string' ? (loc ? `${loc}: ${msg}` : msg) : fallback;
  }
  return err?.message ?? fallback;
}

export default function MoreEditOptionsModal({ isOpen, onClose, pageType = null, resourceId = null }) {
  const { viewer } = useMap();
  const { createComponent: createComponentApi, createPanelSection: createPanelSectionApi, deleteComponent: deleteComponentApi, deletePanelSection: deletePanelSectionApi, updatePageCamera: updatePageCameraApi, updatePageCameraWithDescendants: updatePageCameraWithDescendantsApi } = useApi();
  const lookups = useEditModeLookups();
  const [values, setValues] = useState(DEFAULT_CAMERA);
  const [createComponent, setCreateComponent] = useState(DEFAULT_CREATE_COMPONENT);
  const [createComponentStatus, setCreateComponentStatus] = useState(null); // 'loading' | 'success' | { error: string }
  const [createUiSection, setCreateUiSection] = useState(DEFAULT_CREATE_UI_SECTION);
  const [createUiSectionStatus, setCreateUiSectionStatus] = useState(null); // 'loading' | 'success' | { error: string }
  const [cameraSaveStatus, setCameraSaveStatus] = useState(null); // 'loading' | 'success' | { error: string }
  const [cameraSaveAllStatus, setCameraSaveAllStatus] = useState(null); // 'loading' | 'success' | { error: string }
  const [deleteComponentSlug, setDeleteComponentSlug] = useState('');
  const [deleteComponentStatus, setDeleteComponentStatus] = useState(null); // 'loading' | 'success' | { error: string }
  const [deleteSectionSlug, setDeleteSectionSlug] = useState('');
  const [deleteSectionStatus, setDeleteSectionStatus] = useState(null); // 'loading' | 'success' | { error: string }

  useEffect(() => {
    if (isOpen && viewer) {
      const camera = getCurrentCameraFromViewer(viewer);
      if (camera) {
        setValues(camera);
      }
    }
  }, [isOpen, viewer]);

  const handleCameraChange = (key, e) => {
    const num = parseFloat(e.target.value);
    setValues((prev) => ({ ...prev, [key]: isNaN(num) ? 0 : num }));
  };

  const handleCreateComponentChange = (key, e) => {
    setCreateComponent((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const cameraBody = {
    camera_lat: values.camera_lat,
    camera_lon: values.camera_lon,
    camera_height_m: values.camera_height_m,
    camera_heading: values.camera_heading,
    camera_pitch: values.camera_pitch,
    camera_roll: values.camera_roll,
  };

  const hasPageContext = pageType && (resourceId != null || pageType === 'home');

  const handleCameraSubmit = async (e) => {
    e.preventDefault();
    if (!updatePageCameraApi || !hasPageContext) return;
    setCameraSaveStatus('loading');
    try {
      await updatePageCameraApi(pageType, resourceId ?? undefined, cameraBody);
      setCameraSaveStatus('success');
      setTimeout(() => setCameraSaveStatus(null), 3000);
    } catch (err) {
      setCameraSaveStatus({ error: getErrorMessage(err, 'Failed to save camera') });
    }
  };

  const handleCameraSaveAllSubmit = async (e) => {
    e.preventDefault();
    if (!updatePageCameraWithDescendantsApi || !hasPageContext) return;
    setCameraSaveAllStatus('loading');
    try {
      await updatePageCameraWithDescendantsApi(pageType, resourceId ?? undefined, cameraBody);
      setCameraSaveAllStatus('success');
      setTimeout(() => setCameraSaveAllStatus(null), 3000);
    } catch (err) {
      setCameraSaveAllStatus({ error: getErrorMessage(err, 'Failed to save camera for all children') });
    }
  };

  const handleCreateComponentSubmit = async (e) => {
    e.preventDefault();
    if (!createComponentApi || !createComponent.component_slug?.trim() || !createComponent.kind?.trim()) return;
    setCreateComponentStatus('loading');
    try {
      await createComponentApi({
        slug: createComponent.component_slug.trim(),
        kind: createComponent.kind.trim(),
        name: createComponent.name?.trim() || null,
        description: createComponent.description?.trim() || null,
      });
      setCreateComponentStatus('success');
      setCreateComponent(DEFAULT_CREATE_COMPONENT);
      setTimeout(() => setCreateComponentStatus(null), 3000);
    } catch (err) {
      setCreateComponentStatus({ error: getErrorMessage(err, 'Failed to create component') });
    }
  };

  const handleCreateUiSectionChange = (key, e) => {
    setCreateUiSection((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleCreateUiSectionSubmit = async (e) => {
    e.preventDefault();
    if (!createPanelSectionApi || !createUiSection.slug?.trim()) return;
    setCreateUiSectionStatus('loading');
    try {
      await createPanelSectionApi({
        slug: createUiSection.slug.trim(),
        title: createUiSection.title?.trim() || null,
      });
      setCreateUiSectionStatus('success');
      setCreateUiSection(DEFAULT_CREATE_UI_SECTION);
      setTimeout(() => setCreateUiSectionStatus(null), 3000);
    } catch (err) {
      setCreateUiSectionStatus({ error: getErrorMessage(err, 'Failed to create panel section') });
    }
  };

  const handleDeleteComponent = async (e) => {
    e.preventDefault();
    if (!deleteComponentApi || !deleteComponentSlug?.trim()) return;
    setDeleteComponentStatus('loading');
    try {
      await deleteComponentApi(deleteComponentSlug.trim());
      setDeleteComponentStatus('success');
      setDeleteComponentSlug('');
      setTimeout(() => setDeleteComponentStatus(null), 3000);
    } catch (err) {
      setDeleteComponentStatus({ error: getErrorMessage(err, 'Failed to delete component') });
    }
  };

  const handleDeleteSection = async (e) => {
    e.preventDefault();
    if (!deletePanelSectionApi || !deleteSectionSlug?.trim()) return;
    setDeleteSectionStatus('loading');
    try {
      await deletePanelSectionApi(deleteSectionSlug.trim());
      setDeleteSectionStatus('success');
      setDeleteSectionSlug('');
      setTimeout(() => setDeleteSectionStatus(null), 3000);
    } catch (err) {
      setDeleteSectionStatus({ error: getErrorMessage(err, 'Failed to delete panel section') });
    }
  };

  if (!isOpen) return null;

  const panelSections = lookups?.panelSections ?? [];
  const panelComponents = lookups?.panelComponents ?? [];

  const content = (
    <Modal title="More Edit Options" onClose={onClose} size="medium">
      <div className="more-edit-options-form">
        <form onSubmit={handleCameraSubmit} className="more-edit-options-section">
          <h4 className="more-edit-options-section-title">Camera</h4>
          <div className="more-edit-options-fields">
            {CAMERA_FIELDS.map(({ key, label }) => (
              <div key={key} className="more-edit-options-field">
                <label htmlFor={key}>{label}</label>
                <input
                  id={key}
                  type="number"
                  step="any"
                  value={values[key]}
                  onChange={(e) => handleCameraChange(key, e)}
                  disabled={cameraSaveStatus === 'loading'}
                />
              </div>
            ))}
          </div>
          {cameraSaveStatus === 'success' && (
            <div className="more-edit-options-success">Camera saved successfully.</div>
          )}
          {cameraSaveStatus?.error && (
            <div className="more-edit-options-error">{cameraSaveStatus.error}</div>
          )}
          {cameraSaveAllStatus === 'success' && (
            <div className="more-edit-options-success">Camera saved for this page and all descendants.</div>
          )}
          {cameraSaveAllStatus?.error && (
            <div className="more-edit-options-error">{cameraSaveAllStatus.error}</div>
          )}
          {!hasPageContext && (
            <div className="more-edit-options-error">No page context available. Save is disabled.</div>
          )}
          <div className="more-edit-options-buttons">
            <button
              type="submit"
              className="more-edit-options-submit"
              disabled={cameraSaveStatus === 'loading' || cameraSaveAllStatus === 'loading' || !hasPageContext}
            >
              {cameraSaveStatus === 'loading' ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              className="more-edit-options-submit more-edit-options-submit-secondary"
              onClick={handleCameraSaveAllSubmit}
              disabled={cameraSaveStatus === 'loading' || cameraSaveAllStatus === 'loading' || !hasPageContext}
            >
              {cameraSaveAllStatus === 'loading' ? 'Saving…' : 'Save for all Children'}
            </button>
          </div>
        </form>

        <form onSubmit={handleCreateComponentSubmit} className="more-edit-options-section">
          <h4 className="more-edit-options-section-title">Create Component</h4>
          <div className="more-edit-options-fields more-edit-options-fields-single">
            {CREATE_COMPONENT_FIELDS.map(({ key, label, required }) => (
              <div key={key} className="more-edit-options-field">
                <label htmlFor={`create-${key}`}>
                  {label}
                  {required && <span className="more-edit-options-required"> *</span>}
                </label>
                <input
                  id={`create-${key}`}
                  type="text"
                  value={createComponent[key]}
                  onChange={(e) => handleCreateComponentChange(key, e)}
                  required={required}
                  disabled={createComponentStatus === 'loading'}
                />
              </div>
            ))}
          </div>
          {createComponentStatus === 'success' && (
            <div className="more-edit-options-success">Component created successfully.</div>
          )}
          {createComponentStatus?.error && (
            <div className="more-edit-options-error">{createComponentStatus.error}</div>
          )}
          <button
            type="submit"
            className="more-edit-options-submit"
            disabled={createComponentStatus === 'loading'}
          >
            {createComponentStatus === 'loading' ? 'Creating…' : 'Submit'}
          </button>
        </form>

        <form onSubmit={handleCreateUiSectionSubmit} className="more-edit-options-section">
          <h4 className="more-edit-options-section-title">Create UI Section</h4>
          <div className="more-edit-options-fields more-edit-options-fields-single">
            <div className="more-edit-options-field">
              <label htmlFor="ui-section-slug">
                Slug
                <span className="more-edit-options-required"> *</span>
              </label>
              <input
                id="ui-section-slug"
                type="text"
                value={createUiSection.slug}
                onChange={(e) => handleCreateUiSectionChange('slug', e)}
                required
                disabled={createUiSectionStatus === 'loading'}
              />
            </div>
            <div className="more-edit-options-field">
              <label htmlFor="ui-section-title">Title</label>
              <input
                id="ui-section-title"
                type="text"
                value={createUiSection.title}
                onChange={(e) => handleCreateUiSectionChange('title', e)}
                disabled={createUiSectionStatus === 'loading'}
              />
            </div>
          </div>
          {createUiSectionStatus === 'success' && (
            <div className="more-edit-options-success">Panel section created successfully.</div>
          )}
          {createUiSectionStatus?.error && (
            <div className="more-edit-options-error">{createUiSectionStatus.error}</div>
          )}
          <button
            type="submit"
            className="more-edit-options-submit"
            disabled={createUiSectionStatus === 'loading'}
          >
            {createUiSectionStatus === 'loading' ? 'Creating…' : 'Submit'}
          </button>
        </form>

        <form onSubmit={handleDeleteComponent} className="more-edit-options-section">
          <h4 className="more-edit-options-section-title">Delete Component</h4>
          <p className="more-edit-options-warning">
            Permanently remove a component from the database. All pages will lose this component. This cannot be undone.
          </p>
          <div className="more-edit-options-fields more-edit-options-fields-single">
            <div className="more-edit-options-field">
              <label htmlFor="delete-component-select">Component</label>
              <select
                id="delete-component-select"
                value={deleteComponentSlug}
                onChange={(e) => setDeleteComponentSlug(e.target.value)}
                disabled={deleteComponentStatus === 'loading' || panelComponents.length === 0}
              >
                <option value="">Select a component…</option>
                {panelComponents.map((c) => (
                  <option key={c.slug ?? c.id} value={c.slug ?? ''}>
                    {c.slug ?? c.title ?? c.name ?? 'Unknown'}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {deleteComponentStatus === 'success' && (
            <div className="more-edit-options-success">Component deleted successfully.</div>
          )}
          {deleteComponentStatus?.error && (
            <div className="more-edit-options-error">{deleteComponentStatus.error}</div>
          )}
          <button
            type="submit"
            className="more-edit-options-submit more-edit-options-submit-danger"
            disabled={deleteComponentStatus === 'loading' || !deleteComponentSlug?.trim() || panelComponents.length === 0}
          >
            {deleteComponentStatus === 'loading' ? 'Deleting…' : 'Delete Component'}
          </button>
        </form>

        <form onSubmit={handleDeleteSection} className="more-edit-options-section">
          <h4 className="more-edit-options-section-title">Delete Panel Section</h4>
          <p className="more-edit-options-warning">
            Permanently remove a panel section. All pages will lose this section. This cannot be undone.
          </p>
          <div className="more-edit-options-fields more-edit-options-fields-single">
            <div className="more-edit-options-field">
              <label htmlFor="delete-section-select">Section</label>
              <select
                id="delete-section-select"
                value={deleteSectionSlug}
                onChange={(e) => setDeleteSectionSlug(e.target.value)}
                disabled={deleteSectionStatus === 'loading' || panelSections.length === 0}
              >
                <option value="">Select a section…</option>
                {panelSections.map((s) => (
                  <option key={s.slug ?? s.id} value={s.slug ?? ''}>
                    {s.slug ?? s.title ?? s.name ?? 'Unknown'}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {deleteSectionStatus === 'success' && (
            <div className="more-edit-options-success">Panel section deleted successfully.</div>
          )}
          {deleteSectionStatus?.error && (
            <div className="more-edit-options-error">{deleteSectionStatus.error}</div>
          )}
          <button
            type="submit"
            className="more-edit-options-submit more-edit-options-submit-danger"
            disabled={deleteSectionStatus === 'loading' || !deleteSectionSlug?.trim() || panelSections.length === 0}
          >
            {deleteSectionStatus === 'loading' ? 'Deleting…' : 'Delete Section'}
          </button>
        </form>
      </div>
    </Modal>
  );

  return createPortal(content, document.body);
}
